import {
  atom,
  atomFamily,
  useRecoilState,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";
import { selectorWithoutCache as selector } from "util/RecoilWithoutCache";
import {
  setUserPreference,
  UserData,
  webCommonConfig,
  setPositionModeFunc,
} from "api/common";
import { setDelegateFlag } from "api/trigger";

import {
  TradeUnitEnum,
  PositionModeEnum,
  TimezoneEnum,
  ADL_STATUS,
} from "constants/vars";
import {
  OPActionState,
  OPOnlyReducePositionPresetState,
} from "components/OrderPlacer/store/atom";
import { OrderActionEnum, OrderTriggerTypeEnum } from "constants/order";
import { CurrentBusinessLineState } from "store/data/Base";

import LocalCache, { CacheKey } from "util/LocalCache";
import { LocaleSelector } from "./Base";
import message from "components/common/BitMessage";
import { isSuccess } from "util/Common";
import { Last24HOption, PriceTagEnum } from "constants/base";
import { useState } from "react";
import { HotTradeBarDirectionEnum } from "store/enum";
import {
  // deleteSimulationTradeFlag,
  // getSimulationTradeFlag,
  setLocalCodeStorageNew,
} from "@saas2/utils";
import {
  sortType,
  initData,
} from "components/dialogs/Setting/types/PositionType";
import { isBrowserEnv } from "@app/utils/constants";
import { useMyIntl } from "hooks";
import { CostOrProfitModalState } from "components/Spot/components/SpotRecordBody/Trade/TableList/Components/CostOrProfitModal";
import { ssrSimulationSelector } from "./ssrUserContext";
import { MKK_GUIDE_ERROR_TYPES } from "constants/user";

export type UserPreferenceSetting = {
  backhand: number; // 反手二次确认 0-未选择 1-选择
  contractUnitSet: TradeUnitEnum; // 合约单位设置 0-数量单位 1-名义价值 2-保证金价值
  languageType: number; // 语言类型 0-英语 1-中文 2-日语 3-韩语 4-越南语 5-繁体 6-俄语
  lightningOpenAndClose: number; // 闪电开平仓二次确认 0-未选择 1-选择
  limitPriceOpenAndClose: number; // 限价开平仓二次确认 0-未选择 1-选择
  percentageSet: number; //
  planEntrust: number; // 计划委托二次确认 0-未选择 1-选择
  quantityIncludesDelegated: number; // 数量包含已委托 0-未选择 1-选择
  receiveType: number; // 接受方式 0 全部 1-仅邮件
  unProfitLoss: PriceTagEnum; // 未实现盈亏计算方式 0-市场价格 1-标记价格

  stopProfitStopLoss: number; // 止盈止损 0-未选择 1-选择
  trace: number; // 追踪委托 0-未选择 1-选择  // 后台定义好了改掉这个就可以了
  lossProfit: number; // 移动止盈止损 0-未选择 1-选择  // 后台定义好了改掉这个就可以了

  contractPopup: number; // 合约成交通知 0-选择 1-未选择
  contractNotice: number; // 合约成交提示音通知 0-选择 1-未选择,
  spotNotice: number; // 现货提示音
  spotPopup?: number; // 现货成交通知（0选中，1未选中）
  spotPopSwitch?: number;
  priceAlarmDesktopPop: number; // 价格提醒桌面弹窗 0-选择 1-未选择
  priceAlarmDesktopVoice: number; // 价格提醒桌面声音 0-选择 1-未选择

  positionModeSet: PositionModeEnum; // 单双向持仓模式选择 0-单向持仓 1-双向持仓

  // 由于历史原因，小写的 timezone 用于k线的时区配置，此参数不往后端传，只在前端存储
  timezone: TimezoneEnum;

  // 大写的 timeZone 用于涨跌幅的时区配置，此参数需要往后端传
  timeZone: string;

  // 作为 timeZone 的中间变量，不需要传后端，但是传了也不影响
  benchmarkTimeZone: string;
  spotPlanEntrust: number; // 币币计划委托
  normalEntrust: number; // 币币普通委托二次确认
  marketEntrust: number; // 币币市价委托二次确认
  contractVoiceSwitch: number; // 合约成交提示音开关 0-选择 1-未选择
  contractPopSwitch: number; // 合约成交弹窗开关 0-选择 1-未选择
  externalMarket: number; // 0 选中，1不选中 外部做市商
  hotTradingBarDirection: number; // 0 选中，1不选中 热门交易条方向

  marginOcoSwitch: number; // 杠杆oco
  marginMarketEntrust: number; // 杠杆市价单
  marginNormalEntrust: number; // 杠杆限价单
  marginProfitOrLoss: number;
  marginVoice: number; // 杠杆价格提醒声音
  marginPop: number; // 杠杆价格提醒桌面通知
  spotAndMarginProfitOrLoss: number; //0 选中，1不选中 止盈止损确认
  quickOrderSwitch: number; // 是否打开快速下单面板  0-选择 1-未选择
  limitQuickOrder: number; // 是否打开限价快速下单功能 0-选择 1-未选择
  spotTrailingSwitch: number; // 现货追踪委托
  orderPlaceAdvancedTPSLConfirm: number; // 高级委托止盈止损二次确认
  tpslPriceType: OrderTriggerTypeEnum; // 高级委托止盈止损二次确认
  adlSortShow: ADL_STATUS; // adl 排名展示 0不展示， 1展示
  // positionSortSetting: sortType[] // 仓位排序设置
  // resetPositionSortSetting: boolean // 重置仓位设置
  costOrProfitPrice: number; // 展示《成本价》 or 《保本价》: 0-成本价;1-保本价
  positionSortSetting: sortType[]; // 仓位排序设置
  resetPositionSortSetting: boolean; // 重置仓位设置
  pinTradingSwitch?: number; // PIN码交易开关
  isSettingPin: number; // 是否已经设置pin码 0-未设置 1-已设置
};

export type OriginalUserInfo = {
  antiPhishingCode: string;
  areaId: string;
  areaCode: number;
  createdDate: number;
  email: string;
  familyName: string;
  frozen: number;
  givenName: string;
  mobile: string;
  nickName: string;
  realName: string;
  starRealName: string;
  subAccount: boolean;
  updatedDate: string;
  userId: string;
  userType: number;
  childFlag: boolean;
  aUser: number;
  vipLevel: number;
  demoUType: string; // 0 表示默认，为1表示虚拟账号, 2表示创世计划demo账户 后端改成string了
  expiredIdFlag: string //kyc证件是否过期flag // expiredldFlag=(0=需要提交,1=提交待审核,2=放行,3=审核失败)
};

export type FixedUserInfo = {
  loginName: string;
  phone: string;
  bindPhone: boolean;
  bindEmail: boolean;
  bindGoogle: boolean;
  isFund: boolean;
  fundValue: number;
  fundDisabled: boolean;
  hasRealValidate: boolean;
  openOtc: boolean;
  openContract: boolean;
  identityLevel: number;
  openGtFees: boolean;
  emailEnable: boolean;
  phoneEnable: boolean;
  googleEnable: boolean;
  childFlag: boolean;
  spotPopup?: number; // 现货成交通知（0选中，1未选中）
  spotNotice?: number; // 现货成交通知提示音（0选中，1未选中）
  spotPopSwitch?: number; // 现货价格提醒
  spotVoiceSwitch?: number; // 现货价格提示音
  showUserVip?: boolean;
  kycUpdate?: boolean;
  hotTradingBarDirection?: HotTradeBarDirectionEnum;
  marginOcoSwitch?: number; // 杠杆oco
  marginMarketEntrust?: number; // 杠杆市价单
  marginNormalEntrust?: number; // 杠杆限价单
  marginProfitOrLoss?: number;
  marginVoice?: number; // 杠杆价格提醒声音
  marginPop?: number; // 杠杆价格提醒桌面通知
  userIdentityFlag?: number; // 合约体验券主子账户状态， 100 为有合约体验券账户，7 为当前是体验券子账户
  encryptUserId?: string; // 交易员id
  strategyTrader?: boolean; // 是否是策略交易员
  pinTradingSwitch?: number; // PIN码交易开关
  isSettingPin: number; // 是否已经设置pin码 0-未设置 1-已设置
  registerDate: any;
  usdtVipLevel: number;
  tryVipLevel: number;
  kycLevel: number; // 0,0未认证 1,一级 2,高级认证
  mkkStatus: MKK_GUIDE_ERROR_TYPES | "SUCCESS"; // MKK_GUIDE_ERROR_TYPES
} & OriginalUserInfo;

export function transferUserInfo(data: UserData) {
  let { userInfo, settings, loginName, kycUpdate } = data;
  let fixedPart = {
    loginName: loginName || "",
    phone: settings.mbf === 1 ? userInfo.mobile : "",
    email: settings.ebf === 1 ? userInfo.email : "",
    bindPhone: Boolean(settings.mbf),
    bindEmail: Boolean(settings.ebf),
    bindGoogle: Boolean(settings.gbf),
    //是否设置资金密码
    isFund: Boolean(settings.tbf),
    // 资金密码 验证规则 1 2 3
    fundValue: settings.tpi,
    fundDisabled: Boolean(settings.tpl),
    antiPhishingCode: userInfo.antiPhishingCode,
    hasRealValidate: settings.kf === 2,
    // 是否开通otc 1表示是,0表示否，默认为0
    openOtc: Boolean(settings.oo),
    //是否开通合约 1表示是,0表示否，默认为0
    openContract: Boolean(settings.oc),
    //KYC认证状态 0:无认证 1:一级 2:高级认证
    identityLevel: settings.kf,
    // 开启BFT抵扣手续费  0 否 1 是
    openGtFees: Boolean(settings.ob),
    realName: userInfo.realName,
    emailEnable: Boolean(settings.eef), // 1启用0停用
    phoneEnable: Boolean(settings.mef), // 1启用0停用
    googleEnable: Boolean(settings.gef), // 1启用0停用
    pinTradingSwitch: Boolean(settings.pis), // 1启用0停用
    isSettingPin: Boolean(settings.pst), // 是否已经设置pin码 0-未设置 1-已设置
    childFlag: userInfo.childFlag, // 是否子账户
    kycUpdate: kycUpdate, // 是否子账户
  };

  return Object.assign({}, userInfo, fixedPart);
}

export function createEmptyUserInfo(): FixedUserInfo {
  return {
    aUser: 0,
    vipLevel: 0,
    antiPhishingCode: "",
    areaId: "",
    areaCode: 0,
    bindEmail: false,
    bindGoogle: false,
    bindPhone: false,
    createdDate: 0,
    email: "",
    emailEnable: false,
    familyName: "",
    frozen: 0,
    fundDisabled: false,
    fundValue: 0,
    givenName: "",
    googleEnable: false,
    hasRealValidate: false,
    identityLevel: 0,
    isFund: false,
    loginName: "",
    mobile: "",
    nickName: "",
    openContract: false,
    openGtFees: false,
    openOtc: false,
    phone: "",
    phoneEnable: false,
    realName: "",
    starRealName: "",
    subAccount: false,
    updatedDate: "",
    userId: "",
    userType: 0,
    childFlag: false,
    kycUpdate: false,
    mkkStatus: MKK_GUIDE_ERROR_TYPES.PHONE_ERROR, // MKK_GUIDE_ERROR_TYPES

    spotPopup: 0, // 现货成交通知（0选中，1未选中）
    spotNotice: 0, // 现货成交通知提示音（0选中，1未选中）
    spotPopSwitch: 0, // 现货价格提醒桌面通知
    spotVoiceSwitch: 0, // 现货价格提醒提示音

    marginOcoSwitch: 0, // 杠杆oco
    marginMarketEntrust: 0, // 杠杆市价单
    marginNormalEntrust: 0, // 杠杆限价单
    marginProfitOrLoss: 0,
    marginVoice: 0, // 杠杆价格提醒声音
    marginPop: 0, // 杠杆价格提醒桌面通知
    pinTradingSwitch: 0, // PIN码交易开关
    isSettingPin: 0, // 是否已经设置pin码 0-未设置 1-已设置
  };
}

export enum UserPreferenceSettingEnum {
  REVERSE_POSITION_CONFIRM = "backhand", // 反手
  LIGHTNING_ORDER_CONFIRM = "lightningOpenAndClose", // 市价 === 闪电
  LIMIT_ORDER_CONFIRM = "limitPriceOpenAndClose", // 限价
  PLAN_ORDER_CONFIRM = "planEntrust", // 计划委托
  IS_DELEGATE_INCLUDED = "quantityIncludesDelegated", // 是否包含已委托
  TPSL_ORDER_CONFIRM = "stopProfitStopLoss", // 止盈止损  下面列表用的弹窗
  TRACK_ORDER_CONFIRM = "trace", // 追踪委托    // 后台定义好了改掉这个就可以了
  MOVE_STOP_LOSS_CONFIRM = "lossProfit", // 移动止盈止损    // 后台定义好了改掉这个就可以了

  CONTRACT_POP_UP_CONFIRM = "contractPopup", //合约弹窗
  CONTRACT_POP_UP_NOTICE_CONFIRM = "contractNotice", //合约通知
  TIMEZONE = "timezone", // k 线时区
  BENCHMARK_TIMEZONE = "benchmarkTimeZone", // 涨跌幅基准
  FUTURE_PRICE_ALERT_DESK_VOICE = "contractVoiceSwitch", //桌面提醒声音
  FUTURE_PRICE_ALERT_DESK_POP_UP = "contractPopSwitch", // 桌面提醒弹窗
  Quick_Order_SWITCH = "quickOrderSwitch", // 是否开启市价快速下单 默认是0 关闭
  QUICK_LIMIT_ORDER_SWITCH = "limitQuickOrder", // 是否开启限价快速下单 默认是0 关闭
  SPOT_PLAN_ENTRUST = "spotPlanEntrust", // 币币计划委托
  SPOT_NORMAL_ENTRUST = "normalEntrust", // 币币普通委托二次确认
  SPOT_MARKET_ENTRUST = "marketEntrust", // 币币市价委托二次确认

  WEBHOOK_POP_UP = "webhookSignal", //Webhook订单通知
  WEBHOOK_VOICE = "webhookSound", // Webhook声音通知

  SPOT_POPUP = "spotPopup", // 现货成交通知（0选中，1未选中）
  SPOT_NOTICE = "spotNotice", // 现货成交通知提示音（0选中，1未选中）
  SPOT_POPSWITCH = "spotPopSwitch", // 现货价格提醒桌面通知
  SPOT_VOICESWITCH = "spotVoiceSwitch", // 现货价格提醒提示音

  MARGIN_OCO_SWITCH = "marginOcoSwitch", // 杠杆oco
  MARGIN_MARKET_ENTRUST = "marginMarketEntrust", // 杠杆市价单
  MARGIN_NORMAL_ENTRUST = "marginNormalEntrust", // 杠杆限价单
  MARGIN_PROFIT_OR_LOSS = "marginProfitOrLoss",
  MARGIN_VOICE = "marginVoice", // 杠杆价格提醒声音
  MARGIN_POP = "marginPop", // 杠杆价格提醒桌面通知
  STRATEGY_AUTO_PUBLISH = "strategyAutoPublish", // 策略自动发布设置 (0未选中，1选中）默认关闭
  ORDER_PLACE_ADVANCED_TPSL_CONFIRM = "profitOrLessNormalEntrust", // 高级委托止盈止损二次确认
  SPOTANDMARGINPROFITORLOSS = "spotAndMarginProfitOrLoss", // 现货止盈止损
  SPOTTRAILINGSWITCH = "spotTrailingSwitch",
  // 止盈止损价格类型
  TPSL_PRICE_TYPE = "tpslPriceType",
  ADL_SORT_SHOW = "adlSortShow", // adl 展示开关
  POSION_SORT_SETTING = "positionSortSetting", //排序
  // RESET_POSITION_SORT_SETTING = 'resetPositionSortSetting', //全部止盈止损

  COST_OR_PROFIT_PRICE = "costOrProfitPrice", // 成本价 保本价
  RESET_POSITION_SORT_SETTING = "resetPositionSortSetting", //全部止盈止损
  PIN_TRADING_SWITCH = "pinTradingSwitch", //校验PIN码
  IS_SETTING_PIN = "isSettingPin", //是否已经设置pin码
}

export function createDefaultUserPreferenceSetting(): UserPreferenceSetting {
  return {
    [UserPreferenceSettingEnum.REVERSE_POSITION_CONFIRM]: 0,
    contractUnitSet: 0,
    languageType: 0,
    [UserPreferenceSettingEnum.LIGHTNING_ORDER_CONFIRM]: 0,
    [UserPreferenceSettingEnum.LIMIT_ORDER_CONFIRM]: 0,
    percentageSet: 0,
    [UserPreferenceSettingEnum.PLAN_ORDER_CONFIRM]: 0,
    [UserPreferenceSettingEnum.IS_DELEGATE_INCLUDED]: 1,
    receiveType: 0,
    [UserPreferenceSettingEnum.TPSL_ORDER_CONFIRM]: 0,
    unProfitLoss: 0,
    // 新增一个类型
    [UserPreferenceSettingEnum.TRACK_ORDER_CONFIRM]: 0, // 追踪委托
    [UserPreferenceSettingEnum.MOVE_STOP_LOSS_CONFIRM]: 0, // 移动止盈止损
    [UserPreferenceSettingEnum.CONTRACT_POP_UP_CONFIRM]: 0,
    [UserPreferenceSettingEnum.CONTRACT_POP_UP_NOTICE_CONFIRM]: 0,

    positionModeSet: 2, // 单双向持仓,默认双向   1单 2双

    [UserPreferenceSettingEnum.TIMEZONE]: TimezoneEnum.UTC,
    [UserPreferenceSettingEnum.BENCHMARK_TIMEZONE]: Last24HOption,

    [UserPreferenceSettingEnum.FUTURE_PRICE_ALERT_DESK_POP_UP]: 0,

    [UserPreferenceSettingEnum.SPOT_PLAN_ENTRUST]: 0,
    [UserPreferenceSettingEnum.SPOT_MARKET_ENTRUST]: 0,
    [UserPreferenceSettingEnum.SPOT_NORMAL_ENTRUST]: 0,

    [UserPreferenceSettingEnum.SPOT_POPUP]: 0,
    [UserPreferenceSettingEnum.SPOT_NOTICE]: 0,
    [UserPreferenceSettingEnum.SPOT_POPSWITCH]: 0,
    [UserPreferenceSettingEnum.SPOT_VOICESWITCH]: 0,

    [UserPreferenceSettingEnum.FUTURE_PRICE_ALERT_DESK_VOICE]: 0,
    [UserPreferenceSettingEnum.FUTURE_PRICE_ALERT_DESK_POP_UP]: 0,
    [UserPreferenceSettingEnum.Quick_Order_SWITCH]: 0,
    hotTradingBarDirection: HotTradeBarDirectionEnum.UP, // 热门交易条方向

    [UserPreferenceSettingEnum.WEBHOOK_VOICE]: 0,
    [UserPreferenceSettingEnum.WEBHOOK_POP_UP]: 0,

    [UserPreferenceSettingEnum.MARGIN_OCO_SWITCH]: 0,
    [UserPreferenceSettingEnum.MARGIN_MARKET_ENTRUST]: 0,
    [UserPreferenceSettingEnum.MARGIN_NORMAL_ENTRUST]: 0,
    [UserPreferenceSettingEnum.MARGIN_PROFIT_OR_LOSS]: 0,
    [UserPreferenceSettingEnum.MARGIN_VOICE]: 0,
    [UserPreferenceSettingEnum.MARGIN_POP]: 0,

    [UserPreferenceSettingEnum.STRATEGY_AUTO_PUBLISH]: 0,
    [UserPreferenceSettingEnum.ORDER_PLACE_ADVANCED_TPSL_CONFIRM]: 0,
    tpslPriceType: OrderTriggerTypeEnum.FAIR,
    [UserPreferenceSettingEnum.ADL_SORT_SHOW]: 0, // adl 展示开关，0为隐藏，1为展示
    [UserPreferenceSettingEnum.POSION_SORT_SETTING]: initData,
    [UserPreferenceSettingEnum.RESET_POSITION_SORT_SETTING]: false,
    // [UserPreferenceSettingEnum.RESET_POSITION_SORT_SETTING]:false,
    [UserPreferenceSettingEnum.PIN_TRADING_SWITCH]: 0,
    [UserPreferenceSettingEnum.IS_SETTING_PIN]: 0,
    externalMarket: 0,
  } as unknown as UserPreferenceSetting;
}

export const UserState = atom<FixedUserInfo>({
  key: "base-user-state",
  default: createEmptyUserInfo(),
});

export const IsShowPinState = atom<boolean>({
  key: "is-show-pin-state",
  default: false,
});

export interface FullUserInfo {
  userName: any;
  userInfo: FixedUserInfo;
  userAvatar: string;
  loginName: string;
  displayName: string;
  areaId?: string;
}
/**
 * 用户完整的信息
 */
export const UserFullInfoState = atom<FullUserInfo>({
  key: "full-user-state",
  default: {} as FullUserInfo,
});

export const LoginSelector = selector<boolean>({
  key: "is-login-selector",
  get: ({ get }) => {
    const user = get(UserState);
    return user.userId !== "";
  },
});

/**
 * 通过 API 判断用户是否在模拟盘交易白名单中
 */
export const UserSimulationInfoState = atom<{ simulation: boolean }>({
  key: "user-simulation-info-state",
  default: { simulation: false },
});

export const UserSimulationFlagState = selector<boolean>({
  key: "user-simulation-flag-selector",
  get: ({ get }) => {
    // return get(ssrSimulationSelector) === 'true' || (isBrowserEnv && getSimulationTradeFlag())
    return get(ssrSimulationSelector) === "true";
  },
  set: ({ set }, value) => {
    if (!value) {
      set(ssrSimulationSelector, "false");

      // if (isBrowserEnv) {
      //     deleteSimulationTradeFlag()
      // }
    }
  },
});

/***
 * 该用户是否是交易员
 */

export const isTraderState = atom<boolean>({
  key: "user-state-isTrader",
  default: false,
});

export const UserPreferenceSettingState = atom<UserPreferenceSetting>({
  key: "user-preference-setting-state",
  default: createDefaultUserPreferenceSetting(),
});

export function useUserPreferenceSetting(): [
  UserPreferenceSetting,
  (setting: Partial<UserPreferenceSetting>) => void,
] {
  const t = useMyIntl();

  //获取用户偏好设置state
  const [userPreferenceSetting, setUserPreferenceSetting] = useRecoilState(
    UserPreferenceSettingState
  );
  const isLogin = useRecoilValue(UserState).loginName;
  const [, setCostOrProfitModal] = useRecoilState(CostOrProfitModalState);
  const [isLoading, setIsLoading] = useState(false);

  function baseSetter(setting: Partial<UserPreferenceSetting>) {
    if (UserPreferenceSettingEnum.IS_DELEGATE_INCLUDED in setting) {
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, {
          quantityIncludesDelegated: setting.quantityIncludesDelegated,
        })
      );
    } else if (UserPreferenceSettingEnum.TIMEZONE in setting) {
      // 暂时特殊处理所选周期
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, setting)
      );
      LocalCache.set(CacheKey.TIMEZONE, setting.timezone);
    } else {
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, setting)
      );
    }
  }

  // 设置 setUserPreferenceSetting  api请求
  async function setter(setting: Partial<UserPreferenceSetting>) {
    try {
      if (isLogin) {
        if (UserPreferenceSettingEnum.IS_DELEGATE_INCLUDED in setting) {
          try {
            const data = await setDelegateFlag({
              delegateFlag: setting.quantityIncludesDelegated,
            });
            if (isSuccess(data?.code)) {
              baseSetter(setting);
            } else {
              data.msg && message.error(data.msg);
            }
          } catch (error) {
            console.log("--error: --", error);
          }
        } else if (UserPreferenceSettingEnum.TIMEZONE in setting) {
          baseSetter(setting);
        } else {
          //修改用户偏好设置-请求
          if (isLoading) return;
          setIsLoading(true);
          try {
            const data = await setUserPreference(setting);
            if (isSuccess(data?.code)) {
              baseSetter(setting);
              // 如果修改成本价或者保本价需要提示用户
              if (
                UserPreferenceSettingEnum.COST_OR_PROFIT_PRICE in setting &&
                Object.keys(setting)?.length === 1
              ) {
                message.success(
                  t(
                    setting?.costOrProfitPrice === 1
                      ? "spot_margin_position_pl.spot_pnl.update_profit_success"
                      : "spot_margin_position_pl.spot_pnl.update_cost_success"
                  ) as string
                );
                setCostOrProfitModal(false);
              }
            } else {
              if (UserPreferenceSettingEnum.COST_OR_PROFIT_PRICE in setting) {
                message.error(
                  t(
                    "spot_margin_position_pl.spot_pnl.update_fail",
                    "Switching failed. Try again later."
                  ) as string
                );
              } else {
                data.msg && message.error(data.msg);
              }
            }
          } catch (error) {
            console.log("--error: --", error);
          }
          setIsLoading(false);
        }
      } else {
        baseSetter(setting);
      }
    } catch (error) {
      console.log(error);
    }
  }

  return [userPreferenceSetting, setter];
}
export function useUserPreferenceSettingWithAsyc(
  callback
): [UserPreferenceSetting, (setting: Partial<UserPreferenceSetting>) => void] {
  //获取用户偏好设置state
  const [userPreferenceSetting, setUserPreferenceSetting] = useRecoilState(
    UserPreferenceSettingState
  );
  const isLogin = useRecoilValue(UserState).loginName;

  const [isLoading, setIsLoading] = useState(false);

  function baseSetter(setting: Partial<UserPreferenceSetting>) {
    if (UserPreferenceSettingEnum.IS_DELEGATE_INCLUDED in setting) {
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, {
          quantityIncludesDelegated: setting.quantityIncludesDelegated,
        })
      );
    } else if (UserPreferenceSettingEnum.TIMEZONE in setting) {
      // 暂时特殊处理所选周期
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, setting)
      );
      LocalCache.set(CacheKey.TIMEZONE, setting.timezone);
    } else {
      setUserPreferenceSetting((oldSetting) =>
        Object.assign({}, oldSetting, setting)
      );
    }
  }

  // 设置 setUserPreferenceSetting  api请求
  async function setter(setting: Partial<UserPreferenceSetting>) {
    try {
      if (isLogin) {
        if (UserPreferenceSettingEnum.IS_DELEGATE_INCLUDED in setting) {
          setDelegateFlag({
            delegateFlag: setting.quantityIncludesDelegated,
          }).then((data) => {
            if (isSuccess(data?.code)) {
              baseSetter(setting);
            } else {
              data.msg && message.error(data.msg);
            }
          });
        } else if (UserPreferenceSettingEnum.TIMEZONE in setting) {
          baseSetter(setting);
        } else {
          //修改用户偏好设置-请求
          if (isLoading) return;
          setIsLoading(true);
          setUserPreference(setting)
            .then((data) => {
              if (isSuccess(data?.code)) {
                baseSetter(setting);
                // callback && callback()
              } else {
                data.msg && message.error(data.msg);
              }
            })
            .finally(() => {
              callback && callback();
              setIsLoading(false);
            });
        }
      } else {
        baseSetter(setting);
      }
    } catch (error) {
      console.log(error);
    }
  }

  return [userPreferenceSetting, setter];
}
/**
 * 持仓模式更改函数
 * @returns
 */
export const usePositionModeSet = (): [
  number,
  (setting: Partial<UserPreferenceSetting>) => void,
] => {
  const setUserPreferenceSetting = useSetRecoilState(
    UserPreferenceSettingState
  );
  const businessLine = useRecoilValue(CurrentBusinessLineState);
  const [, setAction] = useRecoilState(OPActionState);
  const setIsReducePosition = useSetRecoilState(
    OPOnlyReducePositionPresetState
  );
  const isLogin = useRecoilValue(UserState).loginName;
  function setPostionModeFunc(setting: Partial<UserPreferenceSetting>): void {
    if (isLogin) {
      // 异步设置positionModeSet的值
      setPositionModeFunc({
        holdMode: setting?.positionModeSet,
        secondBusinessLine: "N/A",
        businessLine: businessLine,
      }).then((data) => {
        if (isSuccess(data?.code)) {
          //设置用户偏好设置选项里的持仓模式的值
          setUserPreferenceSetting((oldSetting) =>
            Object.assign({}, oldSetting, setting)
          );
          // 切换成开仓状态
          setAction(OrderActionEnum.OPEN);
          // 设置只减仓为false
          setIsReducePosition(false);
        } else {
          data.msg && message.error(data.msg);
        }
      });
    } else {
      message.error("未登录");
    }
  }
  return [businessLine, setPostionModeFunc];
};

let webCommonConfigFetching = false;
export const WebCommonConfig = selector({
  key: "web-common-config",
  get: async ({ get }) => {
    get(LoginSelector);
    get(LocaleSelector);
    if (webCommonConfigFetching) {
      return;
    }
    webCommonConfigFetching = true;
    try {
      if (!isBrowserEnv) {
        return {};
      }
      const res = await webCommonConfig();
      if (isSuccess(res?.code)) {
        // 设置用户增长缓存code
        setLocalCodeStorageNew(res?.data?.channleCodeCache || {});
        return res.data;
      }
      return {};
    } catch (error) {
      console.error("web-common-config error:", error);
    } finally {
      webCommonConfigFetching = false;
    }
  },
});

export const FuturesIsCreationDemoAccountState = atom<boolean>({
  key: "futures-is-creation-demo-account-state",
  default: false,
});

export const FuturesIsCreationDemoAccountSelector = selector<boolean>({
  key: "futures-is-creation-demo-account-selector",
  get: ({ get }) => {
    return get(FuturesIsCreationDemoAccountState);
  },
  set: ({ set }, data) => {
    set(FuturesIsCreationDemoAccountState, data);
  },
});

//记录是否使用新的爆仓价计算公式
export const FuturesIsNewLiquidityState = atom<boolean>({
  key: "futures-is-new-liquidity-state",
  default: false,
});
